import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

export interface Audience {
  id: string;
  name: string;
  summary: string;
  traits: string[];
  isSelected: boolean;
}
export interface Voice {
  id: string;
  name: string;
  description: string;
  examples: string[];
  isSelected: boolean;
}
export interface Persona {
  id: string;
  name: string;
  description: string;
  formatMixCarouselPct: number;
  formatMixSinglePct: number;
  isActive: boolean;
}
export interface Theme {
  id: string;
  label: string;
  description: string;
  exampleAngles: string[];
  isActive: boolean;
}
export interface BrandKit {
  id: string;
  palette: { primary: string; secondary: string; accent: string; background: string; text: string };
  fonts: { heading: string; body: string };
  logoUrl: string | null;
  /** OpenAI TTS voice used when the reel content team narrates this brand. */
  ttsVoice: string;
}
export interface Sample {
  id: string;
  personaId: string;
  format: 'carousel' | 'single_image';
  title: string;
  body: string;
}

export interface SetupOutputs {
  audiences: Audience[];
  voices: Voice[];
  personas: Persona[];
  themes: Theme[];
  brandKit: BrandKit | null;
  samples: Sample[];
}

export function useSetupOutputs(projectId: string | undefined, isRunning = false) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ['projects', projectId, 'setup'],
    queryFn: () => api<SetupOutputs>(`/api/projects/${projectId}/setup`),
    // Poll every 2s while a setup run is in progress so each agent's output
    // surfaces as it lands. Stop polling once nothing is running.
    refetchInterval: isRunning ? 2000 : false,
  });
}

export type SetupKind = 'audiences' | 'voices' | 'personas' | 'themes';

export function useDeleteSetupItem(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, id }: { kind: SetupKind; id: string }) =>
      api(`/api/projects/${projectId}/setup/${kind}/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] });
    },
  });
}

export function useRunSetup(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ runId: string; remaining: number }>(`/api/projects/${projectId}/setup`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] });
      qc.invalidateQueries({ queryKey: ['pipeline', 'runs', projectId] });
    },
  });
}

export function useRefreshTeam(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ runId: string; remaining: number; mode: 'refresh' }>(`/api/projects/${projectId}/setup/refresh`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] });
      qc.invalidateQueries({ queryKey: ['pipeline', 'runs', projectId] });
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup', 'counts'] });
    },
  });
}

export function useResetTeam(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ runId: string; remaining: number; mode: 'reset' }>(`/api/projects/${projectId}/setup/reset`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] });
      qc.invalidateQueries({ queryKey: ['pipeline', 'runs', projectId] });
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup', 'counts'] });
    },
  });
}

export function useGenerateOne(projectId: string | undefined, kind: 'audiences' | 'voices' | 'personas' | 'themes') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ item: unknown; remaining: number }>(`/api/projects/${projectId}/setup/${kind}/generate-one`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] });
      qc.invalidateQueries({ queryKey: ['pipeline', 'runs', projectId] });
    },
  });
}

export interface SetupCounts {
  audiences: number;
  voices: number;
  personas: number;
  themes: number;
  samples: number;
}

export interface BriefChangeStatus {
  hasPrevious: boolean;
  briefChanged: boolean;
  rulesChanged: boolean;
  changeChars: number;
}

export function useSetupCounts(projectId: string | undefined) {
  return useQuery({
    enabled: !!projectId,
    queryKey: ['projects', projectId, 'setup', 'counts'],
    queryFn: () => api<{ counts: SetupCounts; change: BriefChangeStatus }>(`/api/projects/${projectId}/setup/counts`),
  });
}


function patchSetupCache(
  qc: ReturnType<typeof useQueryClient>,
  projectId: string | undefined,
  patch: (current: SetupOutputs) => SetupOutputs,
) {
  const key = ['projects', projectId, 'setup'];
  const previous = qc.getQueryData<SetupOutputs>(key);
  if (previous) qc.setQueryData<SetupOutputs>(key, patch(previous));
  return { key, previous };
}

export function useToggleAudience(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api(`/api/projects/${projectId}/setup/audiences/${id}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      }),
    onMutate: async ({ id, active }) => {
      await qc.cancelQueries({ queryKey: ['projects', projectId, 'setup'] });
      return patchSetupCache(qc, projectId, (current) => ({
        ...current,
        audiences: current.audiences.map((a) => (a.id === id ? { ...a, isSelected: active } : a)),
      }));
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] }),
  });
}

export function useToggleVoice(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api(`/api/projects/${projectId}/setup/voices/${id}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      }),
    onMutate: async ({ id, active }) => {
      await qc.cancelQueries({ queryKey: ['projects', projectId, 'setup'] });
      return patchSetupCache(qc, projectId, (current) => ({
        ...current,
        voices: current.voices.map((v) => (v.id === id ? { ...v, isSelected: active } : v)),
      }));
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] }),
  });
}

export function useTogglePersona(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api(`/api/projects/${projectId}/setup/personas/${id}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      }),
    onMutate: async ({ id, active }) => {
      await qc.cancelQueries({ queryKey: ['projects', projectId, 'setup'] });
      return patchSetupCache(qc, projectId, (current) => ({
        ...current,
        personas: current.personas.map((p) => (p.id === id ? { ...p, isActive: active } : p)),
      }));
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] }),
  });
}

export function useToggleTheme(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api(`/api/projects/${projectId}/setup/themes/${id}/active`, {
        method: 'PATCH',
        body: JSON.stringify({ active }),
      }),
    onMutate: async ({ id, active }) => {
      await qc.cancelQueries({ queryKey: ['projects', projectId, 'setup'] });
      return patchSetupCache(qc, projectId, (current) => ({
        ...current,
        themes: current.themes.map((t) => (t.id === id ? { ...t, isActive: active } : t)),
      }));
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(ctx.key, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] }),
  });
}

export function useUpdateBrandKit(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: {
      palette?: BrandKit['palette'];
      fonts?: BrandKit['fonts'];
      ttsVoice?: string;
    }) =>
      api<{ brandKit: BrandKit }>(`/api/projects/${projectId}/brand-kit`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects', projectId, 'setup'] }),
  });
}
